import React, { useMemo, useState } from 'react';
import {
  Box,
  Card,
  Stepper,
  Step,
  StepLabel,
  Button,
  Container,
  Typography,
  Stack,
  Chip,
  Divider,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined';

import { BASEURL } from '../../Constants';
import Step1PersonalInfo from './Step1PersonalInfo';
import Step3Demo from './Step3Demo';
import PentestSimulation from './Step4PentestSimulation';
import Step5Completion from './Step5Completion';

// New order: 1 (Personal Info), 4 (Pentest Simulation), 3 (Platform Demo), 5 (Done)
const steps = ['Personal Info', 'Pentest Simulation', 'Platform Demo', 'Done'];

// —— Light aesthetic ——
const SoftSection = styled('section')(() => ({
  minHeight: '100vh',
  width: '100%',
  background:
    'radial-gradient(1200px 600px at 10% 0%, #f3f7ff 0%, #f7f9fc 30%, #fbfcff 60%, #ffffff 100%)',
}));

const ElevatedCard = styled(Card)(() => ({
  borderRadius: 18,
  backgroundColor: '#ffffff',
  boxShadow: '0 8px 28px rgba(16, 24, 40, 0.08)',
}));

// ——— Custom Step Icon (4 steps) ———
const StepIcon = (props) => {
  const { active, completed, icon } = props;
  const map = {
    1: <PersonOutlineIcon />, // Personal Info
    2: <SmartToyOutlinedIcon />, // Pentest Simulation
    3: <PlayCircleOutlineIcon />, // Platform Demo
    4: <CheckCircleRoundedIcon />, // Done
  };
  return (
    <Box
      sx={{
        width: 36,
        height: 36,
        borderRadius: '50%',
        display: 'grid',
        placeItems: 'center',
        color: completed ? 'success.main' : active ? 'primary.main' : 'text.secondary',
        backgroundColor: completed ? '#E9F8EF' : active ? '#EDF4FF' : '#F3F5F7',
        border: `1px solid ${completed ? '#BCE7CD' : active ? '#C9DBFF' : '#E5E7EB'}`,
      }}
    >
      {map[icon]}
    </Box>
  );
};

function Onboarding() {
  // 0-based step index
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    jobTitle: '',
    platforms: [],
  });
  const [simulationDone, setSimulationDone] = useState(0);
  const navigate = useNavigate();

  // Validation per step
  const canProceedPersonal = useMemo(() => {
    return Boolean(formData.name && formData.company && formData.jobTitle);
  }, [formData]);

  const doSubmit = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const res = await fetch(`${BASEURL}/user/onboarding`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ formData }),
      });
      const result = await res.json();
      if (result?.success) navigate('/pentests');
    } catch (e) {
      console.error('Onboarding submit failed', e);
    }
  };

  const handleNext = () => {
    if (activeStep === steps.length - 1) {
      doSubmit();
    } else {
      setActiveStep((s) => s + 1);
    }
  };

  const handleBack = () => setActiveStep((s) => Math.max(s - 1, 0));
  const handleChange = (e) =>
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  // Next button should be enabled on steps 1 & 2 (indexes 1 and 2),
  // only gated on step 0 (personal info)
  const nextDisabled = activeStep === 0 ? !canProceedPersonal : false;

  return (
    <SoftSection>
      <Container maxWidth="lg" sx={{ pt: 6, pb: 2 }}>
        {/* Brand & tagline */}
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <ShieldOutlinedIcon color="primary" sx={{ fontSize: 28 }} />
            <Typography variant="h6" fontWeight={800}>
              StealthNet AI
            </Typography>
            <Chip size="small" label="Beta" variant="outlined" />
          </Stack>
          <Chip
            icon={<RocketLaunchOutlinedIcon />}
            color="primary"
            label="Launch your first AI Pentest in minutes"
            sx={{ fontWeight: 700 }}
          />
        </Stack>
      </Container>

      {/* Progress */}
      <Container maxWidth="lg" sx={{ pb: 2 }}>
        <ElevatedCard sx={{ p: { xs: 1.5, md: 2.5 } }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label, idx) => (
              <Step key={label}>
                <StepLabel StepIconComponent={StepIcon}>
                  <Typography variant="body2" sx={{ fontWeight: idx === activeStep ? 800 : 600 }}>
                    {label}
                  </Typography>
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </ElevatedCard>
      </Container>

      {/* Content */}
      <Container maxWidth="lg" sx={{ pb: 10 }}>
        <Stack direction={{ xs: 'column-reverse', md: 'row' }} spacing={3}>
          {/* Left: form/cards */}
          <ElevatedCard sx={{ p: { xs: 2, md: 4 }, flex: 1 }}>
            <Stack spacing={2}>
              <Box>
                <Typography variant="h4" fontWeight={900} gutterBottom>
                  {activeStep === 0 && 'Tell us about you'}
                  {activeStep === 1 && (simulationDone ? 'Simulation Complete' : 'Pentest Simulation')}
                  {activeStep === 2 && 'Quick Platform Walkthrough'}
                  {activeStep === 3 && 'You’re all set!'}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {activeStep === 0 &&
                    'We’ll use this to personalize dashboards, reports, and recommendations.'}
                  {activeStep === 1 &&
                    'Run a mini simulation to experience automated recon → scan → insight.'}
                  {activeStep === 2 &&
                    'See how findings, assets, and reports come together—no setup required.'}
                  {activeStep === 3 &&
                    'Nice! Your workspace is configured. You can launch a pentest anytime.'}
                </Typography>
              </Box>

              <Divider />

              <Box>
                {activeStep === 0 && (
                  <Step1PersonalInfo formData={formData} handleChange={handleChange} />
                )}
                {activeStep === 1 && (
                  <PentestSimulation setSimulationDone={setSimulationDone} />
                )}
                {activeStep === 2 && <Step3Demo />}
                {activeStep === 3 && <Step5Completion />}
              </Box>

              {/* Actions */}
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                justifyContent="space-between"
                alignItems={{ xs: 'stretch', sm: 'center' }}
              >
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                    onClick={handleBack}
                    disabled={activeStep === 0}
                    sx={{ borderRadius: 12, px: 2.5, py: 1.25 }}
                  >
                    Back
                  </Button>
                  <Button
                    variant="text"
                    onClick={() => navigate('/pentests')}
                    sx={{ textTransform: 'none' }}
                  >
                    Skip for now
                  </Button>
                </Stack>
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={nextDisabled}
                  endIcon={
                    activeStep === steps.length - 1 ? (
                      <CheckCircleRoundedIcon />
                    ) : (
                      <RocketLaunchOutlinedIcon />
                    )
                  }
                  sx={{ borderRadius: 12, px: 3, py: 1.25, fontWeight: 700 }}
                >
                  {activeStep === steps.length - 1 ? 'Finish' : 'Next'}
                </Button>
              </Stack>
            </Stack>
          </ElevatedCard>

          {/* Right: show ONLY on Personal Info (step index 0) */}
          {activeStep === 0 && (
            <ElevatedCard sx={{ p: { xs: 2, md: 4 }, flex: 0.9, overflow: 'hidden' }}>
              <Stack spacing={1.5}>
                <Typography variant="subtitle1" fontWeight={800}>
                  A fleet of AI Agents built for pentesters
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  External, Web, Internal, Cloud, Social Engineering—configure once and launch targeted tests. Findings roll up into clear, compliance-ready reports.
                </Typography>
                <Box
                  sx={{
                    mt: 1,
                    height: 240,
                    width: '100%',
                    borderRadius: 14,
                    overflow: 'hidden',
                    background: 'linear-gradient(135deg, #EDF4FF 0%, #E9FBF3 100%)',
                    display: 'grid',
                    placeItems: 'center',
                  }}
                >
                  <Stack direction="row" spacing={1}>
                    <Chip icon={<SmartToyOutlinedIcon />} label="External" color="primary" variant="outlined" />
                    <Chip icon={<SmartToyOutlinedIcon />} label="Web App" color="primary" variant="outlined" />
                    <Chip icon={<SmartToyOutlinedIcon />} label="Internal" color="primary" variant="outlined" />
                  </Stack>
                </Box>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <ElevatedCard sx={{ p: 2, flex: 1 }}>
                    <Typography variant="subtitle2" fontWeight={800} gutterBottom>
                      What happens next?
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      We’ll tailor your dashboard, enable agent presets, and surface quick-start playbooks.
                    </Typography>
                  </ElevatedCard>
                  <ElevatedCard sx={{ p: 2, flex: 1 }}>
                    <Typography variant="subtitle2" fontWeight={800} gutterBottom>
                      Need help?
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Take the 2-minute tour in the Demo step or reach support from the sidebar anytime.
                    </Typography>
                  </ElevatedCard>
                </Stack>
              </Stack>
            </ElevatedCard>
          )}
        </Stack>
      </Container>
    </SoftSection>
  );
}

export default Onboarding;